import React, { useState } from "react"
import { useDispatch } from "react-redux"
import styled from "styled-components"
import { logUserEvent } from "../common/firebase.utils"
import { requestPay } from "../common/utils"
import { useUserInfo } from "../hooks/useUserInfo"
import { ReactComponent as CloseButton } from "../images/close-gray.svg"
import { showLoginPopup } from "../store/slices/layerSlice"
import useIsMobile from "../hooks/useIsMobile"
import { ReactComponent as RadioIcon } from "../images/formkit_radio.svg"
import MembershipBadge from "../common/MembershipBadge"
import { PRODUCT_OPTIONS } from "../common/constants"

function PaymentPopup({ onCloseClick }) {
  const { userId, loggedIn } = useUserInfo()
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const [selectedOption, setSelectedOption] = useState("basic")

  const onClickPayNow = () => {
    const selectedProduct = PRODUCT_OPTIONS.find(
      option => option.id === selectedOption
    )
    logUserEvent("click_pay_now", {
      product_id: selectedOption,
      product_price: selectedProduct?.price
    })
    if (loggedIn) {
      requestPay(userId, {
        productId: selectedOption,
        price: selectedProduct?.price,
        productName: selectedProduct?.title
      })
    } else {
      dispatch(
        showLoginPopup({
          message: "로그인 후 결제를 진행할 수 있습니다."
        })
      )
    }
    onCloseClick()
  }

  return (
    <Background>
      <Container>
        <HeaderContainer className="flex-col h-[110px] md:h-[110px] text-white flex pt-10 md:pt-0 items-center text-center md:justify-center gap-2">
          <p className="leading-none font-medium">
            {isMobile
              ? "한 번의 결제로 무제한 사용"
              : "계속 업데이트되는 만개 이상의 실생활 템플릿"}
          </p>
          <p className="font-medium leading-tight text-sm">
            {isMobile
              ? "계속 업데이트되는 만개 이상의 실생활 템플릿"
              : "한 번의 결제로 무제한 사용하세요"}
          </p>
        </HeaderContainer>
        <CloseButton id="close_button" onClick={onCloseClick} />
        <div className="pt-8 pb-6 px-6 md:px-10 space-y-10">
          <div>
            <p className="font-semibold leading-none mb-4">상품 선택</p>
            <div className="flex gap-4 flex-col md:flex-row">
              {PRODUCT_OPTIONS.map(option => (
                <ProductOption
                  key={option.id}
                  isSelected={selectedOption === option.id}
                  onClick={() => setSelectedOption(option.id)}
                >
                  <div className="bg-[#F7F8FA] p-4 rounded-none flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <MembershipBadge membershipType={option.id} />
                      <span className="leading-none font-semibold text-base">
                        {option.title}
                      </span>
                    </div>
                  </div>
                  <div className="bg-white p-4 flex-1 flex flex-col justify-between min-h-[108px]">
                    <ul className="text-sm leading-[1.2] text-left mb-4 space-y-1">
                      {option.description.map((item, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-[#666666] mr-2">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex flex-col items-end gap-3">
                      <div className="flex items-center gap-1">
                        <span className="font-semibold text-lg leading-none">
                          {option.priceText.replace("원", "")}
                        </span>
                        <span className="font-semibold text-base leading-none">
                          원
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="leading-none text-xs text-[#767676]">
                          (부가세 포함)
                        </span>
                      </div>
                    </div>
                  </div>
                </ProductOption>
              ))}
            </div>
          </div>
          <div>
            <p className="font-semibold leading-none mb-4">결제수단</p>
            <div className="h-[48px] px-4 bg-white rounded-lg border border-[#ebebeb] flex items-center">
              <span className="font-medium">
                <RadioIcon className="inline-block mr-2" />
                신용카드
              </span>
            </div>
          </div>
        </div>
        <Buttons>
          <div onClick={onClickPayNow}>결제하기</div>
        </Buttons>
      </Container>
    </Background>
  )
}

const Container = styled.div`
  position: relative;
  background-color: #ffffff;
  width: 562px;
  box-shadow: 0px 4px 32px rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  overflow: hidden;
  #close_button {
    position: absolute;
    top: 1rem;
    right: 1rem;
    z-index: 1;
    fill: #ffffff;
    stroke: #ffffff;
    cursor: pointer;
  }
  @media (max-width: 800px) {
    width: 100%;
    height: 100%;
  }
`
const Buttons = styled.div`
  width: 327px;
  margin: 16px auto 24px;
  > * {
    text-align: center;
    border-radius: 4px;
    cursor: pointer;
  }
  > :first-child {
    color: #ffffff;
    border-radius: 12px;
    background: #5551ff;
    line-height: 3rem;
    font-size: 14px;
  }
  @media (max-width: 800px) {
    width: calc(100% - 48px);
  }
`

const HeaderContainer = styled.div`
  background-image: linear-gradient(
    290.11deg,
    #7869d6 -15.93%,
    #3d3764 14.76%,
    #16122c 45.45%
  );
`

const ProductOption = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border: ${props =>
    props.isSelected ? "1.8px solid #0500FF" : "1px solid #EBEBEB"};
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${props => (props.isSelected ? "#0500FF" : "#CCCCCC")};
  }
`

const Background = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 50;
`

export default PaymentPopup
